import React, { useEffect, useRef, useState } from 'react'
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import * as THREE from 'three'
import { TrackballControls } from "three/examples/jsm/controls/experimental/CameraControls";
import { TransformControls } from "three/examples/jsm/controls/TransformControls";
import { Lut } from "three/examples/jsm/math/Lut";
import './Scene.css';
import { useSelector } from "react-redux";
import { fileStorage } from '../../helpers/utils';

function ScenePrint({ vessel, classes, clearColor, maps, help, orbit }) {
    const mount = useRef(null);
    const printCamera = useSelector(item => item.flowviewer.printCamera);
    const [view, setView] = useState(false);
    const progressID = "progress-" + vessel;
    const scenePrintID = "scene-print-" + vessel;
    let scene;

    useEffect(async () => {
        let width = mount.current.clientWidth;
        let height = mount.current.clientHeight;
        let frameId;

        scene = new THREE.Scene();

        const camera = new THREE.PerspectiveCamera(
            70,
            width / height,
            0.1,
            10000
        );

        help && scene.add(new THREE.AxesHelper(10000));
        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            preserveDrawingBuffer: true
        });
        renderer.outputEncoding = THREE.sRGBEncoding;
        renderer.setClearColor(clearColor);
        renderer.setSize(width, height);

        camera.position.set(100, 100, 100);
        const controls = new TrackballControls(camera, renderer.domElement);
        controls.rotateSpeed = 0.7;
        controls.noZoom = true;
        controls.noPan = true;
        controls.enabled = orbit;

        const control = new TransformControls(camera, renderer.domElement);

        function getLight(color, position, int) {
            const alight = new THREE.PointLight(color, int);
            alight.position.x = position.x;
            alight.position.z = position.y;
            alight.position.y = position.z;
            scene.add(alight);
        }

        getLight(0xffffff, { x: 100, y: 100, z: 100 }, 0.5);
        getLight(0xffffff, { x: -100, y: -100, z: -100 }, 0.5);

        const pointLight = new THREE.AmbientLight(0xffffff, 0.5);
        scene.add(pointLight);

        const lut = new Lut();
        lut.addColorMap("rainbowInvert",
            [
                [0.0, "#960c0c"],
                [0.3, "#960c0c"],
                [0.6, "#f50f0f"],
                [0.7, "#ff3e1c"],
                [0.75, "#fca503"],
                [0.775, "#fcc603"],
                [0.8, "#fffb00"],
                [0.825, "#fcc603"],
                [0.85, "#0dff00"],
                [0.9, "#00ffff"],
                [0.95, "#0000ff"],
                [1, "#0000ff"]
            ]
        );
        lut.setColorMap('rainbowInvert');

        const loader = new GLTFLoader();
        const [coloringJson, stlUrl] = await Promise.all([
            fileStorage.loadFile("coloring").then(response => response.data.text().then(JSON.parse)),
            fileStorage.loadFile("stl").then(response => URL.createObjectURL(new Blob([response.data])))
        ]);

        loader.load(stlUrl, (gltf) => {
            const model = gltf.scene;
            scene.add(model);
            model.traverse(function(mesh) {
                if (mesh.isMesh) {
                    mesh.geometry.center();
                    mesh.geometry.computeVertexNormals();
                    
                    const count = mesh.geometry.attributes.position.count;
                    const colors = new Float32Array(count * 3);
                    colors.fill(1);
                    mesh.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
                    
                    lut.setMax(1);
                    lut.setMin(0);
                    
                    const colorAttr = mesh.geometry.attributes.color;
                    if (coloringJson) {
                        for (let i = 0; i < count; i++) {
                            const colorValue = coloringJson[i];
                            if (colorValue === -1 || colorValue === undefined) {
                                colorAttr.setXYZ(i, 0.25, 0.25, 0.25);
                            } else {
                                const color = lut.getColor(colorValue);
                                if (color) {
                                    colorAttr.setXYZ(i, color.r, color.g, color.b);
                                } else {
                                    colorAttr.setXYZ(i, 0.25, 0.25, 0.25);
                                }
                            }
                        }
                    }
                    
                    mesh.material = new THREE.MeshPhongMaterial({
                        vertexColors: true
                    });
                    scene.add(mesh);
                    setView(true);
                    
                    // Set camera position
                    if (printCamera[vessel]?.position?.x) {
                        camera.position.x = printCamera[vessel].position.x;
                        camera.position.y = printCamera[vessel].position.y;
                        camera.position.z = printCamera[vessel].position.z;
                        camera.rotation.x = printCamera[vessel].rotation.x;
                        camera.rotation.y = printCamera[vessel].rotation.y;
                        camera.rotation.z = printCamera[vessel].rotation.z;
                    } else {
                        camera.position.x = mesh.geometry.boundingBox.max.x - mesh.geometry.boundingBox.min.x;
                        camera.position.y = mesh.geometry.boundingBox.max.y - mesh.geometry.boundingBox.min.y;
                        camera.position.z = mesh.geometry.boundingBox.max.z - mesh.geometry.boundingBox.min.z;
                    }
                }
            });
            
            control.attach(model);
            control.setMode("rotate");
            control.setSpace('world');
            control.enabled = false;
            control.visible = false;
            scene.add(control);
        }, 
        (xhr) => {
            if (!view) {
                const text = Math.round(xhr.loaded / xhr.total * 100);
                if (text === 100) {
                    setView(true);
                } else {
                    const progressBarElement = document.getElementById(progressID);
                    if (progressBarElement) {
                        progressBarElement.innerText = text + "%";
                        progressBarElement.setAttribute("aria-valuenow", text + "%");
                        progressBarElement.setAttribute("style", "width: " + text + "%");
                    }
                }
            }
        },
        (err) => {
            // TODO: log error
        });

        const renderScene = () => {
            renderer.render(scene, camera)
        }

        const handleResize = () => {
            width = mount.current.clientWidth;
            height = mount.current.clientHeight;
            renderer.setSize(width, height);
            camera.aspect = width / height;
            camera.updateProjectionMatrix();
            renderScene()
        }

        const animate = () => {
            renderScene();
            camera.updateMatrixWorld();
            frameId = window.requestAnimationFrame(animate);
            controls.update();
        };

        const start = () => {
            if (!frameId) {
                frameId = requestAnimationFrame(animate);
            }
        };

        const stop = () => {
            cancelAnimationFrame(frameId);
            frameId = null
        };

        mount.current.appendChild(renderer.domElement);
        window.addEventListener('resize', handleResize);
        start();

        return () => {
            stop();
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <div id={scenePrintID}
            className={"scene " + classes}
            ref={mount}>
            {
                !view &&
                <div className="progress progress-barss">
                    <div id={progressID} className="progress-bar progress-bar-striped" role="progressbar"
                        aria-valuenow="0"
                        aria-valuemin="0"
                        aria-valuemax="100">
                        0%
                    </div>
                </div>
            }
        </div>
    );
}

export default ScenePrint;